import { useCallback, useEffect, useState } from 'react';
import { ShowroomOptionSelection, ShowroomPresetSelection, ShowroomSelectionState } from '@/services/showroom';

const STORAGE_KEY_PREFIX = 'bygr-showroom-selections:';

const emptyState: ShowroomSelectionState = { optionSelections: [], presetSelections: [] };

function readFromStorage(slug: string): ShowroomSelectionState {
    if (typeof window === 'undefined') return emptyState;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY_PREFIX + slug);
        if (!raw) return emptyState;
        const parsed = JSON.parse(raw);
        return {
            optionSelections: Array.isArray(parsed.optionSelections) ? parsed.optionSelections : [],
            presetSelections: Array.isArray(parsed.presetSelections) ? parsed.presetSelections : [],
        };
    } catch {
        return emptyState;
    }
}

function writeToStorage(slug: string, value: ShowroomSelectionState): void {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(STORAGE_KEY_PREFIX + slug, JSON.stringify(value));
    } catch {
        // localStorage may be disabled (private browsing, quota exceeded). Selections will stay
        // in memory for the session but won't persist across reloads.
    }
}

// (groupId, roomId) is the identity for an option selection. RoomId is normalized to null when
// the option group isn't room-scoped, so equality comparisons are unambiguous.
function normalizeRoomId(roomId: number | null | undefined): number | null {
    return roomId == null ? null : roomId;
}

function replaceOption(
    list: ShowroomOptionSelection[],
    next: ShowroomOptionSelection
): ShowroomOptionSelection[] {
    const filtered = list.filter((s) => !(s.groupId === next.groupId && s.roomId === next.roomId));
    return [...filtered, next];
}

function replacePreset(
    list: ShowroomPresetSelection[],
    next: ShowroomPresetSelection
): ShowroomPresetSelection[] {
    const filtered = list.filter((s) => !(s.presetGroupId === next.presetGroupId && s.roomId === next.roomId));
    return [...filtered, next];
}

function removePresetMatching(
    list: ShowroomPresetSelection[],
    presetGroupId: number,
    roomId: number | null
): ShowroomPresetSelection[] {
    return list.filter((s) => !(s.presetGroupId === presetGroupId && s.roomId === roomId));
}

export function useShowroomSelections(slug: string) {
    const [state, setState] = useState<ShowroomSelectionState>(() => readFromStorage(slug));

    // Re-hydrate when slug changes (different unit, different storage entry).
    useEffect(() => {
        setState(readFromStorage(slug));
    }, [slug]);

    const persist = useCallback(
        (next: ShowroomSelectionState) => {
            setState(next);
            writeToStorage(slug, next);
        },
        [slug]
    );

    const setOption = useCallback(
        (groupId: number, roomId: number | null, optionId: number, quantity?: number | null) => {
            const next: ShowroomSelectionState = {
                ...state,
                optionSelections: replaceOption(state.optionSelections, {
                    groupId,
                    roomId: normalizeRoomId(roomId),
                    optionId,
                    quantity: quantity ?? null,
                }),
            };
            persist(next);
            return next;
        },
        [persist, state]
    );

    // Apply several option selections atomically. Folding over the current list (rather than
    // calling setOption N times against the same render-time snapshot) is what makes the legacy
    // "identical options across groups" feature persist every group instead of only the last one.
    const setOptions = useCallback(
        (options: ShowroomOptionSelection[]) => {
            const nextOptionSelections = options.reduce(
                (acc, o) => replaceOption(acc, { ...o, roomId: normalizeRoomId(o.roomId) }),
                state.optionSelections
            );
            const next: ShowroomSelectionState = { ...state, optionSelections: nextOptionSelections };
            persist(next);
            return next;
        },
        [persist, state]
    );

    const removeOption = useCallback(
        (groupId: number, roomId: number | null) => {
            const room = normalizeRoomId(roomId);
            const next: ShowroomSelectionState = {
                ...state,
                optionSelections: state.optionSelections.filter(
                    (s) => !(s.groupId === groupId && s.roomId === room)
                ),
            };
            persist(next);
            return next;
        },
        [persist, state]
    );

    const setPreset = useCallback(
        (presetGroupId: number, roomId: number | null, selectedPresetId: number) => {
            const next: ShowroomSelectionState = {
                ...state,
                presetSelections: replacePreset(state.presetSelections, {
                    presetGroupId,
                    roomId: normalizeRoomId(roomId),
                    selectedPresetId,
                }),
            };
            persist(next);
            return next;
        },
        [persist, state]
    );

    const removePreset = useCallback(
        (presetGroupId: number, roomId: number | null) => {
            const next: ShowroomSelectionState = {
                ...state,
                presetSelections: removePresetMatching(state.presetSelections, presetGroupId, normalizeRoomId(roomId)),
            };
            persist(next);
            return next;
        },
        [persist, state]
    );

    // Replace the whole selection state in one atomic write. Used when a single user action changes
    // both option and preset selections together (e.g. picking a style, which also prunes the
    // covered groups' option picks) — doing it in one persist avoids the stale-snapshot clobber two
    // separate setters would cause.
    const setSelections = useCallback(
        (next: ShowroomSelectionState) => {
            persist(next);
            return next;
        },
        [persist]
    );

    const reset = useCallback(() => {
        persist(emptyState);
        return emptyState;
    }, [persist]);

    return { selections: state, setOption, setOptions, removeOption, setPreset, setSelections, removePreset, reset };
}
